// Demoläget i frontend. Backend avgör om installationen är en demo
// (GET /demo/status), så samma bygge fungerar för både kund och demo.
// Utanför demoläget ritar ingenting här något.
import React, { useEffect, useState } from 'react';
import { hämtaDemostatus, återställDemo } from './api/client';
import { useAuth } from './context/AuthContext';

// Skyltens höjd. Layout flyttar ner meny och innehåll lika mycket.
export const DEMO_HÖJD = 34;

export const DEMOTEXT_KORT =
  'Demomiljö. Alla uppgifter är påhittade och återställs varje natt. Skriv inte in riktiga uppgifter.';
export const DEMOTEXT_LÅNG =
  'Fyrskepp Vinimport, dess kunder, kontaktpersoner, producenter och viner är påhittade. ' +
  'Eventuella likheter med verkliga företag, personer eller produkter är oavsiktliga.';

interface Demostatus {
  demo: boolean;
  aterstalld?: string | null;
}

// Frågan ställs en gång per sidladdning och delas av alla som använder hooken.
// Ett misslyckat anrop sparas inte, så att nästa sida försöker igen.
let löfte: Promise<Demostatus> | null = null;
function hämtaStatus(): Promise<Demostatus> {
  if (!löfte) {
    löfte = hämtaDemostatus()
      .then(r => r.data)
      .catch(() => {
        löfte = null;
        return { demo: false };
      });
  }
  return löfte;
}

export function useDemo(): boolean {
  const [demo, setDemo] = useState(false);
  useEffect(() => {
    let aktuell = true;
    hämtaStatus().then(s => { if (aktuell) setDemo(!!s.demo); });
    return () => { aktuell = false; };
  }, []);
  return demo;
}

const GULD = '#E0A83E';
const MÖRK = '#14161A';

export function Demoskylt({ kort }: { kort: boolean }) {
  const { profil } = useAuth();
  const [öppen, setÖppen] = useState(false);
  const [återställer, setÅterställer] = useState(false);
  const [fel, setFel] = useState('');

  useEffect(() => {
    if (!öppen) return;
    const stäng = (e: KeyboardEvent) => { if (e.key === 'Escape') setÖppen(false); };
    window.addEventListener('keydown', stäng);
    return () => window.removeEventListener('keydown', stäng);
  }, [öppen]);

  async function återställ() {
    const ok = window.confirm(
      'Demon töms och fylls på nytt med ursprungsdatan. Allt som lagts in eller ändrats sedan förra återställningen försvinner.',
    );
    if (!ok) return;
    setÅterställer(true);
    setFel('');
    try {
      await återställDemo();
      // Hela sidan laddas om, så att inget gammalt ligger kvar i minnet.
      window.location.reload();
    } catch (err: any) {
      console.error('Kunde inte återställa demon:', err);
      setFel(err?.response?.data?.detail || 'Återställningen misslyckades. Försök igen om en stund.');
      setÅterställer(false);
    }
  }

  return (
    <>
      <div
        role="note"
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, height: `${DEMO_HÖJD}px`, zIndex: 300,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '14px',
          padding: '0 16px', boxSizing: 'border-box',
          background: GULD, color: MÖRK,
          fontFamily: "'Inter', sans-serif", fontSize: '13px', fontWeight: 500,
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
          {kort ? 'Demomiljö med påhittade uppgifter' : DEMOTEXT_KORT}
        </span>
        <button
          type="button"
          onClick={() => setÖppen(true)}
          style={{
            background: 'none', border: 'none', padding: 0, cursor: 'pointer', flexShrink: 0,
            color: MÖRK, fontFamily: "'Inter', sans-serif", fontSize: '13px', fontWeight: 600,
            textDecoration: 'underline',
          }}
        >
          Om demon
        </button>
      </div>

      {öppen && (
        <div
          onClick={() => setÖppen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 400, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Om demon"
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: '520px', boxSizing: 'border-box',
              background: '#1B1D22', border: '1px solid #2A2B2E', borderRadius: '8px',
              padding: '26px 28px', fontFamily: "'Inter', sans-serif", color: '#F0EEE8',
            }}
          >
            <h2 style={{ margin: '0 0 14px', fontFamily: "'Fraunces', serif", fontWeight: 400, fontSize: '22px' }}>Om demon</h2>
            <p style={{ margin: '0 0 12px', fontSize: '14px', lineHeight: 1.55 }}>{DEMOTEXT_LÅNG}</p>
            <p style={{ margin: '0 0 12px', fontSize: '14px', lineHeight: 1.55, color: '#9B9A93' }}>
              Du kan lägga till, ändra och ta bort fritt. Allt återställs varje natt. Inga mejl skickas från demon,
              och den är inte kopplad till något affärssystem. Skriv inte in riktiga uppgifter om kunder eller personer.
            </p>

            {fel && <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#D9695F' }}>{fel}</p>}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: '20px' }}>
              {profil?.roll === 'admin' && (
                <button
                  type="button"
                  onClick={återställ}
                  disabled={återställer}
                  style={{
                    height: '36px', padding: '0 16px', borderRadius: '2px', cursor: återställer ? 'default' : 'pointer',
                    background: 'transparent', border: '1px solid #2A2B2E', color: '#9B9A93',
                    fontFamily: "'Inter', sans-serif", fontSize: '13.5px', opacity: återställer ? 0.6 : 1,
                  }}
                >
                  {återställer ? 'Återställer...' : 'Återställ demon nu'}
                </button>
              )}
              <button
                type="button"
                onClick={() => setÖppen(false)}
                style={{
                  height: '36px', padding: '0 18px', borderRadius: '2px', cursor: 'pointer',
                  background: GULD, border: `1px solid ${GULD}`, color: MÖRK,
                  fontFamily: "'Inter', sans-serif", fontSize: '13.5px', fontWeight: 500,
                }}
              >
                Stäng
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
